import "./dashboard.css";
import { logError, LogCategory } from "utils/sentryLogger";
import React, { useState } from "react";
import { Alert, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { getNodeByStandardPrefix } from "utils/standards";
import { colors, constants, generateRoutePath, getActivityName, logEducatorDashboard, LogLevel } from "utils";
import { trackClickedButton } from "utils/analytics";
import { request } from "hooks/useOkoApi/useOkoApi";
import { GroupCodeButton } from "./GroupCodeButton";
import { CopyGroupSettingsModal } from "./CopyGroupSettingsModal";
import pie_chart_32 from "assets/pie-chart_32.svg";
import info2 from "assets/info2.svg";

export const GroupCodeCard = ({ activity,
  classroomId,
  className,
  code,
  setParentIsLoading,
  sessionLength,
  standardPrefix,
  standards,
  triggerRefresh,
  triggerRefreshAll,
  allCodesToConfig }) => {
  const history = useHistory();
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [updatedSessionLength, setUpdatedSessionLength] = useState(sessionLength);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const isCrazyCalculator = activity === constants.CRAZY_CALCULATOR_ENUM;

  const getStandardInfo = () => {
    if (isCrazyCalculator) {
      return {
        code: null,
        name: getActivityName(constants.CRAZY_CALCULATOR_ENUM),
        isCrazyCalculator: true
      };
    }

    if (!standardPrefix || activity !== constants.CONSENSUS_ENUM) {
      return null;
    }

    if (!standards || standards.length === 0) {
      return null;
    }

    const node = getNodeByStandardPrefix(standards, standardPrefix);

    if (!node || (!node.standard && !node.cluster && !node.domain && !node.grade)) {
      return null;
    }

    // Determine the type based on what's present in the node
    let type = 'STANDARD';
    let name = '';

    if (node.standard) {
      type = 'STANDARD';
      name = node.standard.name;
    } else if (node.cluster) {
      type = 'CLUSTER';
      name = node.cluster.name;
    } else if (node.domain) {
      type = 'DOMAIN';
      name = node.domain.name;
    } else if (node.grade) {
      type = 'GRADE';
      name = `Grade ${node.grade}`;
    }

    return {
      code: standardPrefix,
      name: name,
      type: type,
      node: node,
      isCrazyCalculator: false
    };
  };

  const standardInfo = getStandardInfo();

  // Check if standards are still loading
  const isStandardsLoading = standardPrefix && activity === constants.CONSENSUS_ENUM && (!standards || standards.length === 0);

  const getTooltipContent = () => {
    if (!standardInfo || standardInfo.isCrazyCalculator) {
      return standardInfo?.name || "Select a standard to get started";
    }

    const node = standardInfo.node;
    const parts = [];

    if (node.grade) {
      parts.push(`Grade ${node.grade}`);
    }
    if (node.domain?.name) {
      parts.push(`Domain: ${node.domain.name}`);
    }
    if (node.cluster?.name) {
      parts.push(`Cluster: ${node.cluster.name}`);
    }

    return (
      <>
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && " > "}
            {part}
          </React.Fragment>
        ))}
      </>
    );
  };

  const getSessionDurationOptions = () => {
    return [
      { label: "10-15 Minutes", value: 10 },
      { label: "15-20 Minutes", value: 15 },
      { label: "20-25 Minutes", value: 20 },
      { label: "25-30 Minutes", value: 25 },
      { label: "30-35 Minutes", value: 30 },
    ];
  };

  const handleSelectSessionDuration = async (value) => {
    const previousLength = updatedSessionLength;
    try {
      setIsLoading(true);
      setParentIsLoading(true);
      await request(`group/${code}`, {
        payload: {
          config: {
            activity_type: activity,
            classroom_id: classroomId,
            standard_prefix: standardPrefix,
            session_length: value,
          }
        }
      });
      logEducatorDashboard("Educator dashboard: Session length changed", {
        event: "session_length_changed",
        groupCode: code,
        previousLength,
        newLength: value,
      });
      setUpdatedSessionLength(value);
      setIsLoading(false);
      setParentIsLoading(false);
      triggerRefresh(code);
    } catch (e) {
      setIsLoading(false);
      setParentIsLoading(false);
      const errorMessage = e?.detail || e?.message || "An unknown error occurred.";
      logEducatorDashboard("Educator dashboard: Failed to update session length", {
        level: LogLevel.ERROR,
        event: "session_length_error",
        groupCode: code,
        errorMessage,
      });
      setError(e?.detail || e?.message || "An unknown error occurred.");
      logError("Failed to update session duration", e, {
        category: LogCategory.ERROR,
        groupCode: code,
      });
    }
  };

  const cardAnalyticsContext = () => ({
    page_name: "Dashboard",
    standard_id: standardPrefix || null,
    group_code: code ? code.toUpperCase() : null,
    class: classroomId || null,
    class_name: className || null,
    session_length: updatedSessionLength || null,
  });

  const handleViewAndEdit = () => {
    logEducatorDashboard("Educator dashboard: View & Edit clicked", {
      event: "group_code_edit_clicked",
      groupCode: code,
      classroomId,
      currentStandard: standardPrefix,
    });
    trackClickedButton("View & Edit", cardAnalyticsContext());
    const basePath = generateRoutePath(constants.GROUP_CODE_STANDARD_SEARCH_ROUTE_NAME, { groupCode: code });
    const params = new URLSearchParams();

    // If there's a standard prefix, add it as a URL parameter to pre-select the standard
    if (standardPrefix && !isCrazyCalculator) {
      params.set("standard", standardPrefix);
    }

    // Pass the classId so we can return to the same class after saving
    if (classroomId) {
      params.set("classId", classroomId);
    }

    const queryString = params.toString();
    history.push(queryString ? `${basePath}?${queryString}` : basePath);
  };

  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <button
      style={{
        alignItems: "center",
        backgroundColor: "white",
        border: "1px solid #ABB1BA",
        borderRadius: "8px",
        color: colors.nightlyWoods500,
        display: "flex",
        fontFamily: "revert",
        fontSize: "16px",
        fontWeight: "400",
        gap: "10px",
        justifyContent: "space-between",
        padding: "10px 12px",
        height: "40px",
        width: "175px"
      }}
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      {children}
      <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: "10px", color: colors.blue500 }} />
    </button>
  ));

  return (
    <div style={{
      backgroundColor: "white",
      borderRadius: "12px",
      boxShadow: "0px 1px 3px 0px rgba(14, 26, 49, 0.10), 0px 1px 2px 0px rgba(14, 26, 49, 0.06)",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      padding: "24px",
      width: "482px"
    }}>
      {/* Header with pie chart icon, standard code and name */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", borderBottom: "1px solid #EAEAFC", paddingBottom: "16px", position: "relative" }}>
        <img src={pie_chart_32} alt="Standard" style={{ alignSelf: "center", width: "32px", height: "32px", flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          {isStandardsLoading ? (
            <div style={{
              color: "#B04420",
              fontSize: "10px",
              fontWeight: "700",
              letterSpacing: "0.5px",
              marginBottom: "4px"
            }}>
              LOADING...
            </div>
          ) : (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`standard-tooltip-${code}`}>
                  {getTooltipContent()}
                </Tooltip>
              }
            >
              <div style={{
                display: "inline-block",
                color: "#B04420",
                fontSize: "10px",
                fontWeight: "700",
                letterSpacing: "0.5px",
                marginBottom: "4px",
                cursor: "help"
              }}>
                {standardInfo?.isCrazyCalculator
                  ? "ACTIVITY"
                  : standardInfo
                    ? `${standardInfo.type} - ${standardInfo.code}`
                    : "NO STANDARD SELECTED"}
              </div>
            </OverlayTrigger>
          )}
          {isStandardsLoading ? (
            <div style={{
              color: colors.nightlyWoods950,
              fontSize: "18px",
              fontWeight: "600",
              lineHeight: "1.3",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}>
              <FontAwesomeIcon icon={faSpinner} spin />
              Loading standard...
            </div>
          ) : (
            <div style={{
              color: colors.nightlyWoods950,
              fontSize: "18px",
              fontWeight: "600",
              lineHeight: "1.3"
            }}>
              {standardInfo?.isCrazyCalculator
                ? standardInfo.name
                : standardInfo
                  ? standardInfo.name
                  : "Select a standard to get started"}
            </div>
          )}
        </div>

        {/* Copy to other groups button */}
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`copy-settings-tooltip-${code}`}>
              Copy to other groups
            </Tooltip>
          }
        >
          <button
            aria-label="Copy group code"
            onClick={() => {
              trackClickedButton("Copy Group", cardAnalyticsContext());
              setIsCopyModalOpen(true);
            }}
            style={{
              background: "none",
              border: "none",
              padding: "4px",
              cursor: "pointer",
              position: "absolute",
              top: "-20px",
              right: "-18px"
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M20 9H11C9.89543 9 9 9.89543 9 11V20C9 21.1046 9.89543 22 11 22H20C21.1046 22 22 21.1046 22 20V11C22 9.89543 21.1046 9 20 9Z" stroke="#334155" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5 15H4C3.46957 15 2.96086 14.7893 2.58579 14.4142C2.21071 14.0391 2 13.5304 2 13V4C2 3.46957 2.21071 2.96086 2.58579 2.58579C2.96086 2.21071 3.46957 2 4 2H13C13.5304 2 14.0391 2.21071 14.4142 2.58579C14.7893 2.96086 15 3.46957 15 4V5" stroke="#334155" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </OverlayTrigger>
      </div>

      {/* Session Length */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`session-length-tooltip-${code}`}>
              To provide a natural stopping point, the session concludes after students finish their current question and the time is within this range.
            </Tooltip>
          }
        >
          <img src={info2} alt="Info" style={{ width: "24px", height: "24px", flexShrink: 0, cursor: "help" }} />
        </OverlayTrigger>
        <span style={{ fontSize: "14px", fontWeight: "600", color: colors.nightlyWoods950, flex: 1 }}>
          Session Length:
        </span>
        <Dropdown>
          <Dropdown.Toggle as={CustomToggle} id={`session-length-${code}`}>
            {getSessionDurationOptions().find(opt => opt.value === updatedSessionLength)?.label || "15-20 Minutes"}
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ width: "100%" }}>
            {getSessionDurationOptions().map((option, index) => (
              <Dropdown.Item
                key={index}
                onClick={() => handleSelectSessionDuration(option.value)}
                active={option.value === updatedSessionLength}
              >
                {option.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Group Code */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`group-code-tooltip-${code}`}>
              Click on your group code to copy the direct link to share with your students
            </Tooltip>
          }
        >
          <img src={info2} alt="Info" style={{ width: "24px", height: "24px", flexShrink: 0, cursor: "help" }} />
        </OverlayTrigger>
        <span style={{ fontSize: "14px", fontWeight: "600", color: colors.nightlyWoods950, flex: 1 }}>
          Group Code:
        </span>
        <GroupCodeButton
          code={code}
          disabled={!standardInfo}
          onCopy={() => trackClickedButton("Copy Code", cardAnalyticsContext())}
        />
      </div>

      {/* View & Edit Button */}
      <button
        style={{
          backgroundColor: "#605DEC",
          border: "none",
          borderRadius: "6px",
          color: "white",
          fontSize: "16px",
          fontWeight: "600",
          cursor: "pointer",
          marginTop: "16px",
          height: "40px",
          transition: "background-color 0.2s",
          opacity: isLoading ? 0.5 : 1
        }}
        disabled={isLoading}
        onClick={handleViewAndEdit}
        onMouseEnter={(e) => e.target.style.backgroundColor = "#4745AE"}
        onMouseLeave={(e) => e.target.style.backgroundColor = "#605DEC"}
      >
        View & Edit
      </button>
      {error &&
        <Alert
          style={{ overflowWrap: "break-word", marginTop: "8px" }}
          dismissible
          onClose={() => setError(null)}
          variant="danger"
        >
          {error}
        </Alert>
      }

      {/* Copy Group Settings Modal */}
      <CopyGroupSettingsModal
        isOpen={isCopyModalOpen}
        onClose={() => setIsCopyModalOpen(false)}
        sourceCode={code}
        sourceConfig={{
          activity_type: activity,
          standard_prefix: standardPrefix,
          session_length: updatedSessionLength
        }}
        allCodesToConfig={allCodesToConfig}
        standards={standards}
        onSuccess={() => {
          // Trigger refresh for all codes to update the entire view
          if (triggerRefreshAll) {
            triggerRefreshAll();
          }
        }}
      />
    </div>
  );
};
